import { TypedDocumentNode, gql } from '@apollo/client';
import qs from 'qs';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Box } from '@sorare/core/src/atoms/ui/Box';
import Dialog from '@sorare/core/src/components/dialog';
import {
  FOOTBALL_PLAY_RIVALS,
  FOOTBALL_PLAY_RIVALS_DRAFT_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { isType } from '@sorare/core/src/gql';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { useTypedParams } from '@sorare/core/src/lib/routing';

import { HeaderTeamsMatchup } from 'components/rivals/HeaderTeamsMatchup';

import {
  RivalsFeaturedInviteQuery,
  RivalsFeaturedInviteQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const StyledDialog = styled(Dialog)<{ $backgroundUrl: string }>`
  ${({ $backgroundUrl }) => css`
    background: center / cover no-repeat url(${$backgroundUrl});
  `};
  aspect-ratio: 375 / 812;
  width: 100%;
`;
const StyledVertical = styled(Vertical)`
  padding: var(--double-unit);
  position: absolute;
  left: 0;
  right: 0;
  top: 55%;
  height: 45%;
  justify-content: space-around;
`;
const TeamsMatchupWrapper = styled(Box)`
  width: 100%;
  background-color: rgba(var(--c-rgb-neutral-100), 0.54);
  backdrop-filter: blur(16px);
`;

const RIVALS_FEATURED_INVITE_QUERY = gql`
  query RivalsFeaturedInviteQuery($inviteId: ID!) {
    node(id: $inviteId) {
      id
      ... on FootballRivalsInvite {
        id
        invitedBy {
          id
          featuredInvitePictureUrl
        }
        game {
          slug
          game {
            id
            ...HeaderTeamsMatchup_game
          }
        }
      }
    }
  }
  ${HeaderTeamsMatchup.fragments.game}
` as TypedDocumentNode<
  RivalsFeaturedInviteQuery,
  RivalsFeaturedInviteQueryVariables
>;
const FeaturedInvitePage = () => {
  const { inviteId } = useTypedParams<PageParams>();
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();

  const { data, loading } = useQuery(RIVALS_FEATURED_INVITE_QUERY, {
    variables: {
      inviteId,
    },
  });

  if (!data && loading) {
    return <LoadingIndicator />;
  }

  if (!inviteId || !data?.node || !isType(data.node, 'FootballRivalsInvite')) {
    return null;
  }

  const { invitedBy, game } = data.node;

  const onReject = () => {
    setOpen(false);
    navigate(generatePath(FOOTBALL_PLAY_RIVALS, { slug: game.slug }));
  };
  const onAccept = () => {
    setOpen(false);
    navigate(
      `${generatePath(FOOTBALL_PLAY_RIVALS_DRAFT_SLUG, {
        slug: game.slug,
      })}${qs.stringify(
        { invitesToAccept: [inviteId] },
        { addQueryPrefix: true }
      )}`
    );
  };

  return (
    <StyledDialog
      maxWidth="xs"
      fullWidth
      $backgroundUrl={invitedBy.featuredInvitePictureUrl || ''}
      open={open}
      onClose={onReject}
    >
      <StyledVertical center gap={3}>
        <TeamsMatchupWrapper>
          <HeaderTeamsMatchup game={game.game} displayRankings={false} />
        </TeamsMatchupWrapper>
        <Vertical gap={2}>
          <Button color="secondary" size="medium" onClick={onAccept}>
            <FormattedMessage
              id="FeaturedInvite.acceptRequest"
              defaultMessage="Accept Request"
            />
          </Button>
          <Button color="tertiary" size="medium" onClick={onReject}>
            <FormattedMessage
              id="FeaturedInvite.rejectRequest"
              defaultMessage="Reject Request"
            />
          </Button>
        </Vertical>
      </StyledVertical>
    </StyledDialog>
  );
};
export default FeaturedInvitePage;
